/** Asset manifest fetcher and preload helpers for the Engine app. Provides helpers for asset resolution, manifest fetching, and CDN preloading. */
import { FrontendConstants } from '../common/FrontendConstants';
import { IsBenignNetworkError, IsBotUserAgent } from '../common/Helpers';
import { Settings } from '../common/Settings';
import { Logger } from '@shared/common';

/** File-local constants for AssetsClient. */
const localConstants = {
  logContext: 'AssetsClient',
  crossOriginAnonymous: 'anonymous',
  manifestFile: 'manifest.json',
  imageExtensions: ['png', 'jpg', 'jpeg', 'webp', 'gif', 'svg'],
  scriptExtensions: ['js', 'mjs'],
  styleExtensions: ['css'],
  fontExtensions: ['woff', 'woff2', 'ttf', 'otf'],
  audioExtensions: ['mp3', 'wav', 'ogg'],
  videoExtensions: ['mp4', 'webm', 'ogv', 'mov'],
  jsonExtensions: ['json']
} as const;
/** Client for asset management including manifest fetching, asset resolution, and CDN preloading. */
export class AssetsClient {
  /** Singleton instance. */
  private static instance: AssetsClient;
  /** Prevent external instantiation. */
  private constructor() {}

  /** @returns The singleton AssetsClient instance. */
  public static GetInstance(): AssetsClient {
    return AssetsClient.instance ?? (AssetsClient.instance = new AssetsClient());
  }

  /** Map of asset resolvers by workspace and bundle. Keyed by manifest name, then bundle name, then resolver function. */
  private resolvers: Map<string, Map<string, (logical: string) => string | undefined>> = new Map();

  /**
   * Get the asset resolver for a specific workspace and asset.
   *
   * @param manifest - Workspace manifest to load (e.g., 'lobby/assets').
   * @param bundle - The asset to resolve (e.g., 'Logo.png').
   * @returns Asset resolver function.
   */
  private getResolver(manifest: string, bundle: string): ((logical: string) => string | undefined) | undefined {
    let workspaceResolvers = this.resolvers.get(manifest);
    if (!workspaceResolvers) {
      workspaceResolvers = new Map();
      this.resolvers.set(manifest, workspaceResolvers);
    }
    return workspaceResolvers.get(bundle);
  }

  /**
   * Set the asset resolver for a specific workspace and asset.
   *
   * @param manifest - Workspace manifest to load (e.g., 'lobby/assets').
   * @param bundle - The asset to resolve (e.g., 'Logo.png').
   * @param resolver - Resolver function.
   */
  private setResolver(manifest: string, bundle: string, resolver: (logical: string) => string | undefined): void {
    let workspaceResolvers = this.resolvers.get(manifest);
    if (!workspaceResolvers) {
      workspaceResolvers = new Map();
      this.resolvers.set(manifest, workspaceResolvers);
    }
    workspaceResolvers.set(bundle, resolver);
  }

  /**
   * Preload a list of CDN URLs with <link rel="preload">, optionally setting CORS and SRI.
   *
   * @param items - Array of asset objects with path and optional SRI.
   * @param options - Preload options.
   * @param options.crossorigin - Cross-origin attribute for the preload link.
   * @param options.integrity - Whether to set the integrity attribute.
   * @returns The hrefs whose preload failed (empty when all preloaded); logging is the caller's job (it owns the manifest version needed to tell stale-page churn from a real failure).
   */
  private async preloadAssets(items: { path: string; sri?: string; href?: string }[], options?: { crossorigin?: 'anonymous' | 'use-credentials'; integrity?: boolean }): Promise<string[]> {
    const results = await Promise.all(
      items.map(async (item) => {
        const href = item.href || item.path;
        const as = this.guessAs(href);
        // Videos stream on demand via the <video> element (preload="metadata"); a <link rel=preload>
        // would use the wrong destination and never match the media request, so emit no link for them.
        if (as === 'video') return '';
        const link = document.createElement('link');
        link.rel = FrontendConstants.RelPreload;
        link.as = as;
        link.href = href;
        if (options?.crossorigin) link.crossOrigin = options.crossorigin;
        // SRI is only supported on <script> and <link rel="stylesheet">; preload links with
        // as="image" or as="fetch" ignore `integrity` and the browser logs a warning.
        const sriSupported = as === 'script' || as === 'style';
        if (options?.integrity && item.sri && sriSupported) link.integrity = item.sri;
        document.head.appendChild(link);
        return new Promise<string>((resolve) => {
          link.addEventListener('load', () => {
            // Trigger a decode check for image assets to catch corrupt files early.
            // Non-image assets (script, style, font, json) are fully loaded by the preload itself.
            if (as === 'image') {
              const img = new Image();
              img.onerror = () => Logger.Warn(localConstants.logContext, `Decode failed for ${href}`);
              img.crossOrigin = localConstants.crossOriginAnonymous;
              img.src = href;
            }
            resolve('');
          });
          link.addEventListener('error', () => resolve(href));
        });
      })
    );
    return results.filter((href) => href.length > 0);
  }

  /**
   * Checks whether the manifest version a page is running has been superseded by a newer deploy. Bot renderers (e.g. Googlebot's WRS) cache subresources for days, so after a deploy they request
   * versioned assets that have been evicted - expected churn that should log, not warn. When the check itself fails, staleness is unprovable and the caller keeps the warnings.
   *
   * @param workspaceID - Workspace ID (e.g. "apps/lobby").
   * @param version - The manifest version the page is running.
   * @returns True when the CDN's current latest version differs from the page's version.
   */
  private async isStaleVersion(workspaceID: string, version: string): Promise<boolean> {
    try {
      const base = Settings.ASSETS_URI?.replace(/\/$/, '') || '';
      const latestUrl = `${base}/${workspaceID}/${FrontendConstants.LatestManifestFile}?t=${Date.now()}`;
      const latest = await fetch(latestUrl, { cache: FrontendConstants.CacheNoStore }).then((r) => r.json());
      const current = latest.latest || latest.manifest?.version;
      return Boolean(current) && current !== version;
    } catch {
      return false;
    }
  }

  /**
   * Infer the <link rel="preload"> `as` attribute from a URL's file extension.
   *
   * @param url - Absolute or relative URL.
   * @returns Preload destination: 'image', 'script', 'style', 'font', 'video', or 'fetch'.
   */
  private guessAs(url: string): 'image' | 'script' | 'style' | 'font' | 'video' | 'fetch' {
    const ext = url.split('?')[0].split('#')[0].split('.').pop()?.toLowerCase();
    if (!ext) return 'fetch';
    if ((localConstants.imageExtensions as readonly string[]).includes(ext)) return 'image';
    if ((localConstants.scriptExtensions as readonly string[]).includes(ext)) return 'script';
    if ((localConstants.styleExtensions as readonly string[]).includes(ext)) return 'style';
    if ((localConstants.fontExtensions as readonly string[]).includes(ext)) return 'font';
    if ((localConstants.videoExtensions as readonly string[]).includes(ext)) return 'video';
    // Some browsers warn that as=audio is unsupported for preload; default to 'fetch' to avoid warnings
    if ((localConstants.audioExtensions as readonly string[]).includes(ext)) return 'fetch';
    if ((localConstants.jsonExtensions as readonly string[]).includes(ext)) return 'fetch';
    return 'fetch';
  }

  /**
   * Get the URI for an asset from a specific manifest and bundle, preloading the bundle if necessary.
   *
   * @param manifest - Workspace manifest to load.
   * @param bundle - Bundle name that contains the logo.
   * @param asset - The asset to resolve.
   * @returns Promise resolving to the logo URL or undefined on failure.
   */
  public async GetAssetURI(manifest: string, bundle: string, asset: string): Promise<string | undefined> {
    try {
      const resolver = this.getResolver(manifest, bundle);
      if (!resolver) {
        const json = await this.FetchManifest(manifest);
        const assets = this.CreateAssetsResolver(json, manifest);
        await assets.loadBundle?.(bundle, { crossorigin: localConstants.crossOriginAnonymous, integrity: true });
        this.setResolver(manifest, bundle, assets.resolve);
      }
      return this.getResolver(manifest, bundle)?.(asset);
    } catch (e) {
      // A manifest fetch that fails at the network level (crawler aborts, offline clients) is expected noise; a real fetch failure (server/HTTP) keeps its warning.
      if (IsBotUserAgent() || IsBenignNetworkError(e)) Logger.Log(localConstants.logContext, `Assets preload failed: ${e}`);
      else Logger.Warn(localConstants.logContext, `Assets preload failed: ${e}`);
    }
  }

  /**
   * Fetch a workspace manifest.json from the CDN.
   *
   * @param workspaceID - Workspace ID (e.g., "lobby/assets").
   * @param version - Version string (e.g., "v123abcd"); if omitted, uses latest.json to resolve.
   * @returns Promise resolving to the manifest object.
   */
  public async FetchManifest(
    workspaceID: string,
    version?: string
  ): Promise<{
    version: string;
    basePath: string;
    bundles: Record<string, Record<string, { path: string; hash: string; sri: string; type?: string }>>;
  }> {
    const base = Settings.ASSETS_URI?.replace(/\/$/, '') || '';
    let url = `${base}/${workspaceID}`;
    if (!version) {
      const latestUrl = `${url}/${FrontendConstants.LatestManifestFile}?t=${Date.now()}`;
      const latest = await fetch(latestUrl, { cache: FrontendConstants.CacheNoStore }).then((r) => r.json());
      version = latest.latest || latest.manifest?.version;
    }
    let manifestUrl = `${url}/${version}/${localConstants.manifestFile}`;
    let resp = await fetch(manifestUrl, { cache: FrontendConstants.CacheNoStore });
    if (!resp.ok) {
      try {
        const freshLatest = await fetch(`${url}/${FrontendConstants.LatestManifestFile}?t=${Date.now()}`, { cache: FrontendConstants.CacheNoStore }).then((r) => r.json());
        const freshVersion = freshLatest.latest || freshLatest.manifest?.version;
        if (freshVersion && freshVersion !== version) {
          version = freshVersion;
          manifestUrl = `${url}/${version}/${localConstants.manifestFile}`;
          resp = await fetch(manifestUrl, { cache: FrontendConstants.CacheNoStore });
        }
      } catch (e) {
        Logger.Debug(localConstants.logContext, `FetchManifest retry failed: ${e}`);
      }
    }
    if (!resp.ok) throw new Error(`Failed to fetch manifest for ${workspaceID} (${version}): ${resp.status}`);
    return resp.json();
  }

  /**
   * Given a workspace manifest, return helpers to resolve asset URLs and preload bundles.
   *
   * @param manifest - Manifest object containing bundles and asset info.
   * @param manifest.version - Manifest version string.
   * @param manifest.basePath - Base path for assets.
   * @param manifest.bundles - Bundles mapping.
   * @param workspaceID - Optional workspace ID the manifest came from; enables the stale-version check that downgrades preload failures from a superseded deploy to plain logs.
   * @returns Object with resolve and loadBundle helpers.
   */
  public CreateAssetsResolver(
    manifest: { version: string; basePath: string; bundles: Record<string, Record<string, { path: string; hash: string; sri: string; type?: string }>> },
    workspaceID?: string
  ): {
    resolve: (logical: string) => string | undefined;
    loadBundle: (bundleName: string, options?: { crossorigin?: 'anonymous' | 'use-credentials'; integrity?: boolean }) => Promise<boolean>;
  } {
    const bundles = manifest.bundles || {};
    const origin = (Settings.ASSETS_URI || '').replace(/\/$/, '');
    const withHref = (list: { path: string; hash: string; sri: string; type?: string }[]) => list.map((it) => ({ ...it, href: `${origin}${it.path}` }));
    const bundleAbs: Record<string, { path: string; hash: string; sri: string; type?: string; href: string }[]> = Object.fromEntries(
      Object.entries(bundles).map(([k, v]) => [k, withHref(Object.values(v))])
    );
    const self = this;

    return {
      /**
       * Resolve a logical filename to a hashed path.
       *
       * @param logical - Logical asset filename.
       * @returns Asset URL or undefined.
       */
      resolve(logical: string): string | undefined {
        for (const v of Object.values(bundles)) {
          if (v[logical]) return `${origin}${v[logical].path}`;
        }
        return undefined;
      },
      /**
       * Load a named bundle by injecting preload links for all items in that bundle.
       *
       * Preload failures from a superseded manifest version (a stale cached page after a deploy, typical of bot renderers) are logged once at Log level; failures on the current version keep
       * their per-asset warnings.
       *
       * @param bundleName - Name of the bundle.
       * @param options - Preload options.
       * @param options.crossorigin - Cross-origin attribute for the preload link.
       * @param options.integrity - Whether to set the integrity attribute.
       * @returns Promise resolving to true if all assets are preloaded.
       */
      async loadBundle(bundleName: string, options?: { crossorigin?: 'anonymous' | 'use-credentials'; integrity?: boolean }): Promise<boolean> {
        const list = bundleAbs[bundleName] || [];
        const failed = await self.preloadAssets(list, options);
        if (failed.length === 0) return true;
        if (workspaceID && (await self.isStaleVersion(workspaceID, manifest.version))) {
          // Stale page after a deploy: the versioned assets it requests are evicted, so this is expected churn - one plain log instead of one warning per asset.
          Logger.Log(localConstants.logContext, `Skipped ${failed.length} preload failure(s) from stale manifest version ${manifest.version} (a newer deploy has superseded this page)`);
        } else {
          for (const href of failed) Logger.Warn(localConstants.logContext, `Preload failed for ${href}`);
        }
        return false;
      }
    };
  }
}
